import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiJson } from '@/lib/api';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Info } from 'lucide-react';

interface CloneCampaignModalProps {
    campaignId: number;
    campaignName: string;
    retailerId: number | null;
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: (newCampaignId: number) => void;
}

interface BookingCycle {
    id: number;
    name: string;
    retailerId?: number;
    status?: string;
}

export function CloneCampaignModal({
    campaignId,
    campaignName,
    retailerId,
    isOpen,
    onClose,
    onSuccess,
}: CloneCampaignModalProps) {
    const [selectedCycleId, setSelectedCycleId] = useState<string>('');
    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Fetch booking cycles filtered by retailer
    const cycleQueryUrl = retailerId
        ? `/api/booking-cycles?retailerId=${retailerId}`
        : '/api/booking-cycles';

    const { data: cycles, isLoading: cyclesLoading } = useQuery<BookingCycle[]>({
        queryKey: ['/api/booking-cycles', retailerId],
        queryFn: async () => apiJson(cycleQueryUrl),
        enabled: isOpen,
    });

    // Clone mutation
    const cloneMutation = useMutation({
        mutationFn: async ({ targetCycleId }: { targetCycleId: number }) => {
            const response = await fetch(`/api/campaigns/${campaignId}/clone`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ targetCycleId }),
                credentials: 'include',
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to clone campaign');
            }

            return response.json();
        },
        onSuccess: (data) => {
            toast({
                title: 'Campaign cloned successfully',
                description: `New campaign created with ID ${data.newCampaignId}`,
            });

            // Invalidate campaigns query to refresh list
            queryClient.invalidateQueries({ queryKey: ['/api/campaigns'] });

            onSuccess?.(data.newCampaignId);
            handleClose();
        },
        onError: (error: Error) => {
            toast({
                title: 'Clone failed',
                description: error.message,
                variant: 'destructive',
            });
        },
    });

    const handleClose = () => {
        setSelectedCycleId('');
        onClose();
    };

    const handleClone = () => {
        if (!selectedCycleId) {
            toast({
                title: 'Select a target cycle',
                description: 'Please select a target cycle for the cloned campaign.',
                variant: 'destructive',
            });
            return;
        }

        cloneMutation.mutate({ targetCycleId: parseInt(selectedCycleId, 10) });
    };

    const clonedName = `Clone of ${campaignName}`;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Clone Campaign</DialogTitle>
                    <DialogDescription>
                        Create a copy of this campaign in a different cycle.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                    {/* Target Cycle Selection */}
                    <div className="grid gap-2">
                        <Label htmlFor="targetCycle">Target Cycle</Label>
                        <Select
                            value={selectedCycleId}
                            onValueChange={setSelectedCycleId}
                            disabled={cyclesLoading}
                        >
                            <SelectTrigger id="targetCycle">
                                <SelectValue placeholder={cyclesLoading ? "Loading cycles..." : "Select a cycle"} />
                            </SelectTrigger>
                            <SelectContent>
                                {cycles?.map((cycle) => (
                                    <SelectItem key={cycle.id} value={String(cycle.id)}>
                                        {cycle.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Cloned Name Preview */}
                    <div className="grid gap-2">
                        <Label>New Campaign Name</Label>
                        <div className="text-sm text-muted-foreground bg-muted px-3 py-2 rounded-md">
                            {clonedName}
                        </div>
                    </div>

                    {/* Info Box */}
                    <div className="flex gap-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-md border border-blue-200 dark:border-blue-800">
                        <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                            This will create a new campaign by copying the saved campaign snapshot.
                            Packs will not be re-applied. The cloned campaign can be edited afterwards.
                        </p>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose} disabled={cloneMutation.isPending}>
                        Cancel
                    </Button>
                    <Button onClick={handleClone} disabled={!selectedCycleId || cloneMutation.isPending}>
                        {cloneMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Clone
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
